import axios from 'axios'
import toCamelCaseKeys from 'camelcase-keys'
import history from 'utils/history'
import localStorageService from 'services/storage'
import qs from 'qs'

class AxiosService {
  constructor() {
    axios.defaults.baseURL = window?._env_?.API_URL || process.env.API_URL
    axios.defaults.params = axios.defaults.params || {}
    axios.defaults.withCredentials = true
    axios.defaults.paramsSerializer = params => {
      const processedParams = { ...params }

      Object.keys(processedParams).forEach(key => {
        const value = processedParams[key]

        if (Array.isArray(value)) {
          processedParams[key] = value.map(item =>
            typeof item === 'object' && item !== null ? JSON.stringify(item) : item,
          )
        } else if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
          processedParams[key] = JSON.stringify(value)
        }
      })

      return qs.stringify(processedParams, {
        arrayFormat: 'brackets',
        encode: true,
      })
    }
    axios.interceptors.request.use(
      config => {
        config.withCredentials = true
        if (typeof window !== 'undefined' && window.location && window.location.pathname) {
          config.headers = config.headers || {}
          config.headers['X-Screen-Context'] = window.location.pathname
        }
        return config
      },
      error => Promise.reject(error),
    )

    axios.interceptors.response.use(this.ensureKeysCase(toCamelCaseKeys), undefined)
    axios.interceptors.response.use(undefined, this.errorHandler)
  }

  ensureKeysCase(handler) {
    const forbiddenUrls = ['rentals/hostsAndListingsLocations']

    return config => {
      if (forbiddenUrls.includes(config.config.url)) return config
      if (config.data && !config.config.skipKeysConversion) {
        config.data = handler(config.data, { deep: true })
      }
      return config
    }
  }

  errorHandler(error) {
    const isAxiosError = ![typeof error, typeof error.response, typeof error.config].includes('undefined')

    if (isAxiosError && error.response?.status === 401) {
      console.error('[Request Interceptor] Unauthorized Error | Redirecting to login page')

      // IVX-11142 — the session is gone, so drop the boot-wave hint. The next
      // load then fires currentUser alone instead of five calls that can only 401.
      localStorageService.clearSession()
      history.push('/login')
      return Promise.reject(error)
    }

    console.error(`[Request Interceptor] ${isAxiosError ? 'API' : 'Non-API'} Error | `, error)
    return Promise.reject(error)
  }

  sanitizeApiUrl(url) {
    return `/api/${url.replace(/^\//, '')}`
  }

  request(config, isCrashable = true, isErrorable = true) {
    return axios({
      ...config,
      isCrashable,
      isErrorable,
      url: this.sanitizeApiUrl(config.url),
    })
  }

  get(url, config) {
    return axios.get(this.sanitizeApiUrl(url), config)
  }

  post(url, data, config) {
    return axios.post(this.sanitizeApiUrl(url), data, config)
  }

  put(url, data, config) {
    return axios.put(this.sanitizeApiUrl(url), data, config)
  }

  delete(url, config) {
    return axios.delete(this.sanitizeApiUrl(url), config)
  }
}

export default new AxiosService()
